import type {
  BlockType,
  _Block,
} from '@backmarket/http-api/src/api-specs-content/models/block'
import type { EmailCollectorApiProps } from '@backmarket/http-api/src/api-specs-content/models/email-collector'
import type { RichText } from '@backmarket/http-api/src/api-specs-content/models/rich-text'
import type {
  SectionBannerApiProps,
  SectionBannerBackgroundApiProps,
} from '@backmarket/http-api/src/api-specs-content/models/section-banner'
import type { BackgroundProps } from '@backmarket/nuxt-layer-cms/Background.types'
import type { EmailCollectorProps } from '@backmarket/nuxt-layer-cms/EmailCollector.types'
import type { SectionBannerProps } from '@backmarket/nuxt-layer-cms/SectionBanner.types'
import { BLOCKS } from '@contentful/rich-text-types'

import { buildPropsWithCorrectLinks } from './link/buildPropsWithCorrectLinks'

function mapToBackground(
  props: SectionBannerBackgroundApiProps,
): BackgroundProps {
  if (
    props.sectionBannerImageBackground &&
    'image' in props.sectionBannerImageBackground
  ) {
    return {
      background: {
        image: props.sectionBannerImageBackground.image,
        mode: props.sectionBannerImageBackground.mode,
      },
    }
  }

  if (props.sectionBannerMonochromeBackground?.theme) {
    return {
      background: {
        theme: props.sectionBannerMonochromeBackground.theme,
      },
    }
  }

  return { background: undefined }
}

function mapEmailCollectorProps(
  apiProps: EmailCollectorApiProps,
): EmailCollectorProps {
  const {
    sectionBannerImageBackground,
    sectionBannerMonochromeBackground,
    ...rest
  } = apiProps

  const { background } = mapToBackground({
    sectionBannerImageBackground,
    sectionBannerMonochromeBackground,
  })

  return { ...rest, background }
}

function mapSectionBannerProps(
  apiProps: SectionBannerApiProps,
): SectionBannerProps {
  const {
    sectionBannerImageBackground,
    sectionBannerMonochromeBackground,
    ...rest
  } = apiProps

  const { background } = mapToBackground({
    sectionBannerImageBackground,
    sectionBannerMonochromeBackground,
  })

  const title =
    apiProps.titleV2 ||
    (apiProps.title ? buildSimpleRichText(apiProps.title, 'h2') : undefined)

  const description =
    apiProps.descriptionV2 ||
    (apiProps.description
      ? buildSimpleRichText(apiProps.description, 'p')
      : undefined)

  return { ...rest, background, title, description }
}

// TODO: map BlockTypeProps to ApiProps
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Mapper = (params: any) => any
const blockPropsMappers: Partial<Record<BlockType, Mapper>> = {
  EmailCollector: mapEmailCollectorProps,
  SectionBanner: mapSectionBannerProps,
}

export function formatBlock(block: _Block, currentUrl: URL) {
  const propsWithCorrectLinks = buildPropsWithCorrectLinks({
    props: block.props,
    currentUrl,
  })

  const blockMapper = blockPropsMappers[block.type]

  if (!blockMapper) return { ...block, props: propsWithCorrectLinks }

  return { ...block, props: blockMapper(propsWithCorrectLinks) }
}

function buildSimpleRichText(text: string, heading: 'h2' | 'p'): RichText {
  return {
    content: [
      {
        content: [
          {
            data: {},
            marks: [],
            nodeType: 'text',
            value: text,
          },
        ],
        data: {},
        nodeType: heading === 'h2' ? BLOCKS.HEADING_2 : BLOCKS.PARAGRAPH,
      },
    ],
    data: {},
    nodeType: BLOCKS.DOCUMENT,
  }
}
